import type { WorkDayResponse } from "../api/schemas";
import type { BreakQualification, ClockEventKind, ClockState } from "../domain/clock";
import { openDaysToCorrect } from "../domain/openDays";
import type { OpenDayThreshold } from "../domain/openDays";
import { isValidTimeZone, parseInstant, toEpochSeconds, toLocalDate } from "../domain/time";
import type { EpochSeconds, LocalDate } from "../domain/time";
import { projectWorkDays, replayClockState, selectEffectiveEvents } from "../domain/workday";
import type { ClockEvent, WorkDay } from "../domain/workday";
import type { ClockSnapshot, LocalEvent } from "./model";
import { isOwnEvent } from "./ownEvents";

/*
 * Local state (spec 8.1.2): the server snapshot plus the events recorded on this device that the
 * snapshot does not contain yet. The API returns working days, not raw events, so the snapshot's
 * blocks are turned back into equivalent clock events and projected together with local events.
 */

/**
 * Creates identifiers for reconstructed events that sort before every real UUIDv7 at the same
 * instant (UUIDv7 starts with the timestamp, so real identifiers are never all zero).
 *
 * @param sequence - Running number.
 * @returns The identifier.
 */
function syntheticId(sequence: number): string {
  const id = `00000000-0000-7000-8000-${sequence.toString(16).padStart(12, "0")}`;

  return id;
}

/**
 * Reconstructs clock events from working days of the API. The reconstruction yields the same
 * blocks and state when projected again; contiguous shifts merge into one, which changes neither
 * durations nor the state.
 *
 * A running segment that started in the same second as the evaluation has no length and is
 * missing from the blocks; the state of the server (`finalState`) restores it for the open day.
 *
 * @param days - Working days in date order.
 * @param finalState - Clock state reported by the server with the days.
 * @param fallbackTimeZoneId - Zone of events whose day names a zone this device does not know.
 * @param runningBreakQualification - Declared kind of a running break that has no block yet (F-57).
 * @returns Equivalent clock events in chronological order.
 */
export function eventsFromWorkDays(
  days: readonly WorkDayResponse[],
  finalState: ClockState,
  fallbackTimeZoneId = "UTC",
  runningBreakQualification: BreakQualification | null = null,
): ClockEvent[] {
  const events: ClockEvent[] = [];
  const push = (
    kind: ClockEventKind,
    occurredAt: EpochSeconds,
    timeZoneId: string,
    breakQualification: BreakQualification | null = null,
  ): void => {
    events.push({
      id: syntheticId(events.length + 1),
      kind,
      occurredAt,
      // A zone the device's ICU data does not know would make every render of the clock screen throw.
      timeZoneId: isValidTimeZone(timeZoneId) ? timeZoneId : fallbackTimeZoneId,
      breakQualification,
    });
  };

  for (const day of days) {
    let previous: { kind: "Work" | "Break"; end: EpochSeconds; isOpen: boolean } | null = null;
    const blocks = [...day.blocks].sort(
      (left, right) => parseInstant(left.start) - parseInstant(right.start),
    );

    for (const block of blocks) {
      const start = parseInstant(block.start);
      const end = parseInstant(block.end);

      if (previous?.end !== start) {
        if (previous !== null) {
          push("ClockOut", previous.end, day.timeZoneId);
        }

        push("ClockIn", start, day.timeZoneId);

        if (block.kind === "Break") {
          push("BreakStart", start, day.timeZoneId, block.breakQualification);
        }
      } else if (block.kind === "Break") {
        // A break after work, or a second break right after one (zero-length work in between).
        if (previous.kind === "Break") {
          push("BreakEnd", start, day.timeZoneId);
        }

        push("BreakStart", start, day.timeZoneId, block.breakQualification);
      } else if (previous.kind !== block.kind) {
        push("BreakEnd", start, day.timeZoneId);
      } else {
        // Two adjacent work blocks come from a zero-length break, which the projection omits.
        push("BreakStart", start, day.timeZoneId);
        push("BreakEnd", start, day.timeZoneId);
      }

      previous = { kind: block.kind, end, isOpen: block.isOpen };
    }

    if (previous === null) {
      // A shift without blocks: clocked in at the evaluation instant, or in and out at once.
      const start = parseInstant(day.firstStart);
      push("ClockIn", start, day.timeZoneId);

      if (day.isClosed) {
        push("ClockOut", start, day.timeZoneId);
      } else if (finalState === "OnBreak") {
        push("BreakStart", start, day.timeZoneId, runningBreakQualification);
      }
    } else if (day.isClosed) {
      push("ClockOut", previous.end, day.timeZoneId);
    } else if (!previous.isOpen) {
      // The day's last end lies after its last block: a further shift of the day began in the
      // evaluation second. Without it, the gap since the last block counted as work (F-57).
      const lastEnd = parseInstant(day.lastEnd);

      if (finalState !== "Off" && lastEnd > previous.end) {
        push("ClockOut", previous.end, day.timeZoneId);
        push("ClockIn", lastEnd, day.timeZoneId);

        if (finalState === "OnBreak") {
          push("BreakStart", lastEnd, day.timeZoneId, runningBreakQualification);
        }
      } else if (finalState === "OnBreak" && previous.kind === "Work") {
        push("BreakStart", previous.end, day.timeZoneId, runningBreakQualification);
      } else if (finalState === "Working" && previous.kind === "Break") {
        push("BreakEnd", previous.end, day.timeZoneId);
      }
    }
  }

  return events;
}

/**
 * Converts a local event to a domain clock event.
 *
 * @param event - The local event.
 * @returns The clock event.
 */
export function toClockEvent(event: LocalEvent): ClockEvent {
  const clockEvent: ClockEvent = {
    id: event.id,
    kind: event.kind,
    occurredAt: event.occurredAt,
    timeZoneId: event.timeZoneId,
    projectId: event.projectId,
    breakQualification: event.breakQualification ?? null,
  };

  return clockEvent;
}

/**
 * Checks whether a local event is already contained in a snapshot. Confirmed events are contained
 * if the snapshot was requested after the confirmation.
 *
 * @param event - The local event.
 * @param snapshot - The snapshot or `null`.
 * @returns `true` if the snapshot already reflects the event.
 */
function isReflected(event: LocalEvent, snapshot: ClockSnapshot | null): boolean {
  const reflected =
    snapshot !== null &&
    event.syncStatus === "synced" &&
    event.syncedAtMs !== null &&
    event.syncedAtMs < snapshot.fetchedAtMs;

  return reflected;
}

/**
 * Returns the clock events of a snapshot. If the server reports an open shift that the snapshot's
 * days do not contain (the shift began before the loaded range, for example in a snapshot stored
 * before F-11), the shift is reopened at the server's last event: the server state takes precedence
 * over the local copy of the working days, so the clock never offers "Kommen" while the server
 * reports the person clocked in (F-11, ADR-0126). Local events recorded since are applied on top.
 *
 * @param snapshot - The snapshot.
 * @param timeZoneId - Time zone of a reopened shift.
 * @returns The events, and whether they come from the snapshot's days alone.
 */
function snapshotEvents(
  snapshot: ClockSnapshot,
  timeZoneId: string,
): { events: readonly ClockEvent[]; complete: boolean } {
  const { state, since } = snapshot.clockState;
  const runningBreak = snapshot.runningBreakQualification ?? null;
  const events = eventsFromWorkDays(snapshot.days, state, timeZoneId, runningBreak);
  const replayed = replayClockState(events);

  if (state === "Off" || since === null || !replayed.ok || replayed.value !== "Off") {
    const complete = { events, complete: true };
    return complete;
  }

  const occurredAt = parseInstant(since);
  const kinds: readonly ClockEventKind[] =
    state === "OnBreak" ? ["ClockIn", "BreakStart"] : ["ClockIn"];
  const opening = kinds.map((kind, index): ClockEvent => ({
    id: syntheticId(events.length + index + 1),
    kind,
    occurredAt,
    timeZoneId,
    breakQualification: kind === "BreakStart" ? runningBreak : null,
  }));
  const reopened = { events: [...events, ...opening], complete: false };

  return reopened;
}

/** Local view of the recent working days. */
export interface LocalTimeline {
  /** Working days projected from snapshot and local events. */
  readonly days: readonly WorkDay[];
  /** Clock state after the last event. */
  readonly state: ClockState;
  /** Instant of the last event, or `null` without events. */
  readonly since: EpochSeconds | null;
  /** Declared kind of the running break (ADR-0092); `null` unless on a break. */
  readonly breakQualification: BreakQualification | null;
  /** Whether local and server data could be combined without conflict. */
  readonly isConsistent: boolean;
}

/**
 * Combines the snapshot with local events and projects the working days up to `asOf`.
 *
 * If the combination is not a valid event sequence (for example a snapshot requested while a
 * synchronization was in flight already contains a confirmed event), the confirmed local events
 * are left out, then the snapshot; rejected events never count. A shift the server reports open
 * but the snapshot's days miss is reopened from the server state, and the timeline is marked
 * inconsistent until a synchronization loads the shift (F-11).
 *
 * @param snapshot - Server snapshot or `null`.
 * @param localEvents - Events recorded on this device.
 * @param asOf - Evaluation instant; the projection uses the later of it and the last effective event (F-99).
 * @param timeZoneId - Time zone of a shift reopened from the server state.
 * @returns The local timeline.
 */
export function buildLocalTimeline(
  snapshot: ClockSnapshot | null,
  localEvents: readonly LocalEvent[],
  asOf: EpochSeconds,
  timeZoneId: string,
): LocalTimeline {
  const fromSnapshot =
    snapshot === null ? { events: [], complete: true } : snapshotEvents(snapshot, timeZoneId);
  const base = fromSnapshot.events;
  // Events recorded on behalf of others (T-170) never change the own timeline.
  const ownEvents = localEvents.filter(isOwnEvent);
  const unreflected = ownEvents
    .filter((event) => event.syncStatus !== "rejected" && !isReflected(event, snapshot))
    .map(toClockEvent);
  const pendingOnly = ownEvents.filter((event) => event.syncStatus === "pending").map(toClockEvent);
  const attempts: readonly (readonly ClockEvent[])[] = [
    [...base, ...unreflected],
    [...base, ...pendingOnly],
    pendingOnly,
  ];

  for (const [index, events] of attempts.entries()) {
    const ordered = selectEffectiveEvents(events, []);
    const last = ordered.ok ? ordered.value[ordered.value.length - 1] : undefined;
    // The device's view counts every recorded event: the screen clock ticks and can lag the event
    // just recorded, a server event can carry a device clock slightly ahead (F-99).
    const projected = projectWorkDays(events, [], Math.max(asOf, last?.occurredAt ?? asOf));
    const state = ordered.ok ? replayClockState(ordered.value) : null;

    if (projected.ok && ordered.ok && state?.ok === true) {
      const timeline: LocalTimeline = {
        days: projected.value,
        state: state.value,
        since: last?.occurredAt ?? null,
        breakQualification:
          state.value === "OnBreak" ? (last?.breakQualification ?? "Unclassified") : null,
        isConsistent: index === 0 && fromSnapshot.complete,
      };
      return timeline;
    }
  }

  const empty: LocalTimeline = {
    days: [],
    state: "Off",
    since: null,
    breakQualification: null,
    isConsistent: false,
  };
  return empty;
}

/**
 * Returns the clock state of the local timeline and the instant it began.
 *
 * @param timeline - The local timeline.
 * @returns State and start of the current state (`null` if unknown).
 */
export function localClockState(timeline: LocalTimeline): {
  state: ClockState;
  since: EpochSeconds | null;
} {
  const clock = { state: timeline.state, since: timeline.since };

  return clock;
}

/**
 * Returns the current working day: the open day (the day its shift began, ADR-0007), otherwise
 * the day of today, otherwise the day of a shift that began earlier and ended today. A night shift
 * clocked out shortly after midnight therefore stays the current working day until the next
 * midnight, like a day shift until the end of its day (F-17). The clock screen shows it, and
 * {@link localOpenDaysToCorrect} leaves it out while it began yesterday or today, but only for a day
 * without the server's threshold: then a night shift whose "Gehen" the device has not sent yet does
 * not count as open. A day with a threshold counts from `incompleteFrom` on, whatever the current
 * working day is (F-13).
 *
 * @param timeline - Local timeline.
 * @param today - Local date of today in `timeZone`.
 * @param timeZone - Time zone of the clock screen, in which the end of a shift is placed on a day.
 * @returns The day or `null`.
 */
export function currentWorkDay(
  timeline: LocalTimeline,
  today: LocalDate,
  timeZone: string,
): WorkDay | null {
  const last = timeline.days[timeline.days.length - 1];

  if (last !== undefined && !last.isClosed) {
    return last;
  }

  const todays = timeline.days.find((day) => day.date === today);

  if (todays !== undefined) {
    return todays;
  }

  const endedToday =
    last?.closedAt != null && toLocalDate(last.closedAt, timeZone) === today ? last : null;

  return endedToday;
}

/**
 * Returns the working day of the shift that is still running on the local timeline (snapshot plus
 * the events recorded on this device, like the clock screen), or `null` while clocked out. The
 * working day is the day the shift began (ADR-0007), so after midnight it can lie before today
 * (F-17). The day depends on the events alone: the timeline is evaluated at the latest instant they
 * and the snapshot know, without a clock.
 *
 * @param snapshot - Server snapshot or `null`.
 * @param localEvents - Events recorded on this device.
 * @param timeZone - Time zone of a shift reopened from the server state (the clock screen's).
 * @returns The working day of the running shift.
 */
export function localRunningShiftDay(
  snapshot: ClockSnapshot | null,
  localEvents: readonly LocalEvent[],
  timeZone: string,
): LocalDate | null {
  const known = snapshot === null ? 0 : toEpochSeconds(snapshot.fetchedAtMs);
  const asOf = localEvents
    .filter(isOwnEvent)
    .reduce((latest, event) => Math.max(latest, event.occurredAt), known);
  const timeline = buildLocalTimeline(snapshot, localEvents, asOf, timeZone);
  const last = timeline.days[timeline.days.length - 1];
  const date = last === undefined || last.isClosed ? null : last.date;

  return date;
}

/**
 * Returns the server's thresholds of the open days stored in a snapshot (F-13); none in snapshots
 * of earlier app versions or from older servers.
 *
 * @param snapshot - Server snapshot or `null`.
 * @returns The thresholds.
 */
function serverThresholds(snapshot: ClockSnapshot | null): OpenDayThreshold[] {
  const details = snapshot?.clockState.openDayDetails ?? [];
  const thresholds = details.map((detail) => ({
    date: detail.date,
    incompleteFrom: parseInstant(detail.incompleteFrom),
  }));

  return thresholds;
}

/**
 * Returns the open days the person is asked to correct, seen from a timeline: the server's open
 * days from the server's threshold on, the same instant from which the server reports them as
 * incomplete and reminds of them (F-13); without a threshold every open day except the current
 * working day while it began yesterday or today (`openDaysToCorrect`, F-11). The clock screen lists
 * them on its timeline with the local events, the icon badge counts them on the server snapshot
 * alone ({@link countOpenDaysToCorrect}, F-14).
 *
 * @param snapshot - Server snapshot or `null`.
 * @param timeline - Timeline of the snapshot, with or without the local events.
 * @param today - Local date of today in the time zone of the clock screen.
 * @param now - Current instant.
 * @param timeZone - Time zone of the clock screen ({@link currentWorkDay}).
 * @returns The dates to correct.
 */
export function localOpenDaysToCorrect(
  snapshot: ClockSnapshot | null,
  timeline: LocalTimeline,
  today: LocalDate,
  now: EpochSeconds,
  timeZone: string,
): LocalDate[] {
  const day = currentWorkDay(timeline, today, timeZone);
  const dates = openDaysToCorrect({
    openDays: snapshot?.clockState.openDays ?? [],
    thresholds: serverThresholds(snapshot),
    currentDay: day?.date ?? null,
    today,
    now,
  });

  return dates;
}

/**
 * Counts the open days to correct for the icon badge (F-14): the days the clock screen lists
 * ({@link localOpenDaysToCorrect}), taken from the stored server snapshot alone like the badge as a
 * whole (ADR-0037 no. 3). With the server's threshold (F-13) a forgotten "Gehen" of a day shift
 * counts from the next day on and a night shift running past midnight only once it can no longer be
 * running; without it a shift counts once it began before yesterday.
 *
 * @param snapshot - Stored server snapshot.
 * @param now - Current instant.
 * @param timeZone - Time zone of the clock screen (the employment's, otherwise the device's).
 * @returns The number of open days to correct.
 */
export function countOpenDaysToCorrect(
  snapshot: ClockSnapshot,
  now: EpochSeconds,
  timeZone: string,
): number {
  const today = toLocalDate(now, timeZone);
  const timeline = buildLocalTimeline(snapshot, [], now, timeZone);
  const count = localOpenDaysToCorrect(snapshot, timeline, today, now, timeZone).length;

  return count;
}
